// Consolidated Certificate Service
// Implements consolidated certificates with 10,000 share maximum per certificate
// Accounts for share transfers and provides complete certificate lifecycle management
// Uses API endpoints - NO getServiceRoleClient() in browser

import { svgCertificateGenerator } from './svgCertificateGenerator';
import { notificationService } from './notificationService';
import { CompanyAccountService } from './services/companyAccountService';
import type { CompanyAccount } from '../types/company';
import { getServerSessionId, clearServerSession } from './serverSession';

// API helper function for certificate service operations
async function callCertificateAPI(action: string, params: Record<string, any> = {}): Promise<any> {
 const sessionId = getServerSessionId();
 const headers: Record<string, string> = { 'Content-Type': 'application/json' };
 if (sessionId) {
 headers['X-Session-Id'] = sessionId;
 }
 const response = await fetch('/api/admin/certificate-service', {
 method: 'POST',
 headers,
 body: JSON.stringify({ action, ...params })
 });

 if (!response.ok) {
 // A 401 here means the admin's server session expired (24h hard cap / 2h
 // inactivity). Clear the stale local session and surface a clear, actionable
 // message instead of a silent console error, so the admin knows to re-login.
 if (response.status === 401) {
 clearServerSession();
 throw new Error('Your admin session has expired. Please log in again and retry.');
 }
 throw new Error(`Certificate API error: ${response.status}`);
 }

 return response.json();
}

export interface CertificateAdminSummary {
 allUsers: number;
 hasId: number;
 noId: number;
 hasCerts: number;
 noCerts: number;
 needsRegen: number;
 approved: number;
 withShares: number;
 noShares: number;
 liveWhole: number;
 onCerts: number;
}

export interface CertificateAdminBalances {
 users: UserShareBalance[];
 summary: CertificateAdminSummary | null;
 incomplete: boolean;
 error: string | null;
}

export interface UserShareBalance {
 user_id: number;
 username: string;
 full_name: string;
 email: string;
 physical_address: string | null;
 purchased_shares: number;
 commission_bonus_shares: number; // 15% commission shares from referrals
 gift_bonus_shares?: number; // birthday/presale gift bonus (bonus_shares on shares_purchased=0 rows); already folded into net_shares by the view
 converted_shares: number; // USDT commission converted to shares
 shares_sent: number;
 shares_received: number;
 net_shares: number;
 net_whole_shares: number;
 certificates_needed: number;
 // Flagging fields for certificate validation
 existing_certificates: number; // Count of existing active & approved certificates
 certificated_shares: number; // Total shares on existing certificates
 needs_regeneration: boolean; // True if certificated_shares != net_whole_shares
 share_discrepancy: number; // net_whole_shares - certificated_shares (positive = missing, negative = excess)
 last_certificate_approved_at: string | null; // Timestamp of last admin-approved certificate
 // KYC status fields (added for admin filtering)
 has_kyc: boolean; // True if user has KYC record
 kyc_status?: string; // KYC status: pending, completed, rejected, expired
 has_id_number: boolean; // True if id_number_encrypted is filled
}

export interface ConsolidatedCertificate {
 id: string;
 user_id: number;
 certificate_number: string;
 shares_count: number;
 shares_at_issuance: number;
 issue_date: string;
 status: 'active' | 'issued' | 'revoked'; // Include 'issued' status
 approval_status: 'pending_approval' | 'approved' | 'rejected';
 revoked_at: string | null;
 revocation_reason: string | null;
 physical_address: string | null;
 certificate_type: string;
 is_legacy: boolean;
 certificate_data: {
 file_url: string;
 generation_metadata: any;
 generated_at: string;
 file_size: number;
 security_hash: string;
 };
 created_at: string;
 updated_at: string;
}

export interface ShareCalculationBreakdown {
 user_id: number;
 username: string;
 full_name: string;
 purchased_shares: number;
 commission_bonus_shares: number;
 shares_received: number;
 shares_sent: number;
 net_shares: number;
 net_whole_shares: number;
 fractional_shares: number;
 certificates_needed: number;
}

export interface CertificateGenerationResult {
 success: boolean;
 certificates?: ConsolidatedCertificate[];
 error?: string;
 revoked_certificates?: string[];
 share_breakdown?: ShareCalculationBreakdown;
}

export class ConsolidatedCertificateService {
 private readonly MAX_SHARES_PER_CERTIFICATE = 10000;

 /**
 * Calculate user's net share balance accounting for all sources
 * Uses the UNIFIED share calculation service for consistency
 */
 async calculateUserShareBalance(userId: number): Promise<ShareCalculationBreakdown | null> {
 try {
 // Get user info via API (username/full_name fallback)
 const userResult = await callCertificateAPI('getUserInfo', { userId });

 if (userResult.error || !userResult.data) {
 console.error('Error fetching user:', userResult.error);
 return null;
 }

 const user = userResult.data;

 // SOURCE OF TRUTH: read the canonical user_share_balances view — the SAME
 // figure the admin panel list and the My Shares page show. The previous
 // client-side calculateUserShares() over-counted by adding presaleCommissionShares
 // (commission_transactions.share_commission) ON TOP of the already-materialised
 // COMMISSION_LEVEL_* purchase rows, so certificates printed more shares than the
 // profile (e.g. 1437 vs 1434). Display-only fix: this can never inflate supply.
 const balResult = await callCertificateAPI('getUserShareBalanceView', { userId });

 if (balResult.error || !balResult.data) {
 console.error('Error fetching user_share_balances view:', balResult.error);
 return null;
 }

 const v = balResult.data;
 const net_shares = parseFloat(String(v.net_shares || 0));
 const net_whole_shares = Number(v.net_whole_shares || 0);
 const certificates_needed = Number(
 v.certificates_needed || Math.ceil(net_whole_shares / this.MAX_SHARES_PER_CERTIFICATE)
 );

 console.log(`[CertificateService] Share calculation for user ${userId} (via user_share_balances VIEW):`, {
 net_shares,
 net_whole_shares,
 certificates_needed
 });

 return {
 user_id: userId,
 username: v.username || user.username,
 full_name: v.full_name || user.full_name,
 purchased_shares: parseFloat(String(v.purchased_shares || 0)),
 commission_bonus_shares: parseFloat(String(v.commission_bonus_shares || 0)),
 shares_received: parseFloat(String(v.shares_received || 0)),
 shares_sent: parseFloat(String(v.shares_sent || 0)),
 net_shares,
 net_whole_shares,
 fractional_shares: net_shares - net_whole_shares,
 certificates_needed
 };

 } catch (error) {
 console.error('Error calculating user share balance:', error);
 // Propagate session-expiry so the UI can prompt re-login instead of showing
 // a misleading "failed to calculate balance" message.
 if (error instanceof Error && error.message.includes('session has expired')) {
 throw error;
 }
 return null;
 }
 }

 /**
 * Get all users with their share balances and KYC status
 * Uses API endpoint with batched fetching
 */
 async getAllUserShareBalances(): Promise<CertificateAdminBalances> {
 try {
 console.log('Loading user share balances via API...');

 // API handles all the batched fetching and KYC merging server-side
 const result = await callCertificateAPI('getAllUserShareBalances', { includeKyc: true });
 const users = Array.isArray(result.data) ? result.data : [];

 console.log(`Loaded ${users.length} user share balances`);
 return {
  users,
  summary: result.summary || null,
  incomplete: !!result.incomplete,
  error: result.error ? String(result.error) : null
 };

 } catch (error) {
 console.error('Error getting all user share balances:', error);
 return {
  users: [],
  summary: null,
  incomplete: true,
  error: error instanceof Error ? error.message : 'Failed to load user share balances'
 };
 }
 }

 /**
 * Revoke all active certificates for a user
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 async revokeUserCertificates(
 userId: number,
 reason: string
 ): Promise<{ success: boolean; revoked_count: number; error?: string }> {
 try {
 const result = await callCertificateAPI('revokeUserCertificates', { userId, reason });
 return result;
 } catch (error) {
 console.error('Error revoking user certificates:', error);
 return {
 success: false,
 revoked_count: 0,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Generate consolidated certificates for a user
 * Automatically revokes old certificates and creates new ones based on current share balance
 * Uses API endpoints - NO getServiceRoleClient() in browser
 */
 async generateConsolidatedCertificates(
 userId: number,
 physicalAddress?: string
 ): Promise<CertificateGenerationResult> {
 try {
 // Calculate user's current share balance
 const shareBreakdown = await this.calculateUserShareBalance(userId);
 if (!shareBreakdown) {
 return { success: false, error: 'Failed to calculate user share balance' };
 }

 // Check if user has any whole shares to certificate
 if (shareBreakdown.net_whole_shares < 1) {
 return {
 success: false,
 error: `User has ${shareBreakdown.net_shares} shares (${shareBreakdown.net_whole_shares} whole shares). Minimum 1 whole share required for certificate generation.`,
 share_breakdown: shareBreakdown
 };
 }

 // Phase 4b (Design A): SPLIT company-attributed shares into their OWN certificate
 // set(s) per legal entity, so COMPANY shares are NEVER folded into the director's
 // PERSONAL certificate. The COMPANY buckets come from PER-PURCHASE attribution
 // (aureus_share_purchases.purchaser_type='COMPANY' + company_id); the PERSONAL
 // remainder = net_whole_shares - Σ(company whole shares).
 //
 // Live-safe: until the Phase 1 columns are applied there are ZERO COMPANY rows, so
 // company_total = 0 and behaviour is IDENTICAL to before this change.
 const breakdownResult = await callCertificateAPI('getCompanyShareBreakdown', { userId });
 if (breakdownResult.error || !breakdownResult.data) {
 return { success: false, error: `Failed to load company share breakdown: ${breakdownResult.error || 'no data'}`, share_breakdown: shareBreakdown };
 }
 const companyBuckets: Array<{ company_id: string; resolved: boolean; legal_name: string | null; registration_number: string | null; address: { street: string; city: string; province: string; postalCode: string; countryCode: string } | null; company_shares: number }>
 = breakdownResult.data.companyBuckets || [];
 const companyTotalShares = Number(breakdownResult.data.company_total_shares || 0);

 // FAIL-CLOSED CONSERVATION GUARD (Constitution §3/§5/§10/§15): never invent, drop
 // or mis-attribute a share. Abort BEFORE any revoke/generate if the split cannot be
 // proven exact and every company bucket resolved.
 const unresolvedBucket = companyBuckets.find(b => !b.resolved);
 if (unresolvedBucket) {
 return { success: false, error: `A COMPANY-attributed purchase references company ${unresolvedBucket.company_id} that could not be resolved; refusing to generate certificates (company shares must not be folded into the personal certificate).`, share_breakdown: shareBreakdown };
 }
 const fractionalBucket = companyBuckets.find(b => !Number.isInteger(b.company_shares) || b.company_shares < 0);
 if (fractionalBucket) {
 return { success: false, error: `Company bucket ${fractionalBucket.company_id} has a non-whole/negative share count (${fractionalBucket.company_shares}); refusing to generate certificates.`, share_breakdown: shareBreakdown };
 }
 const personalWholeShares = shareBreakdown.net_whole_shares - companyTotalShares;
 if (!Number.isInteger(companyTotalShares) || personalWholeShares < 0 || (personalWholeShares + companyTotalShares) !== shareBreakdown.net_whole_shares) {
 return { success: false, error: `Share conservation check failed (personal ${personalWholeShares} + company ${companyTotalShares} != total ${shareBreakdown.net_whole_shares}); refusing to generate certificates.`, share_breakdown: shareBreakdown };
 }

 // KYC GATE: Verify user has completed KYC before generating certificates
 // SECURITY: Certificates require valid ID number — refuse if KYC not completed
 const kycGateResult = await callCertificateAPI('getKycData', { userId });
 const kycGateData = kycGateResult.data;

 if (!kycGateData || !kycGateData.id_number_encrypted || kycGateData.id_number_encrypted.length === 0) {
 return {
 success: false,
 error: 'KYC not completed. User must submit their ID number via KYC verification before certificates can be generated. Please ask the user to complete KYC first.',
 share_breakdown: shareBreakdown
 };
 }

 // Get user details including account_type for company detection via API
 const userResult = await callCertificateAPI('getUserFullDetails', { userId });

 if (userResult.error || !userResult.data) {
 return { success: false, error: 'User not found' };
 }

 const user = userResult.data;

 // Check if this is a company account and fetch company details if so
 let companyData: CompanyAccount | null = null;
 let isCompanyCertificate = false;

 if (user.account_type === 'company') {
 console.log('Company account detected for user', userId, '- fetching company details');
 const companyResult = await CompanyAccountService.getCompanyByPrimaryDirector(userId);
 if (companyResult.success && companyResult.data) {
 companyData = companyResult.data;
 isCompanyCertificate = true;
 console.log('Company found:', companyData.legalName, '- Registration:', companyData.registrationNumber);
 } else {
 console.log('User has company account_type but no company found - using individual details');
 }
 }

 // Use provided address or fall back to user's country of residence
 const address = physicalAddress || user.country_of_residence || 'Address not provided';

 // Revoke all existing active certificates
 const revocationResult = await this.revokeUserCertificates(
 userId,
 `Share balance update: New consolidated certificates generated with ${shareBreakdown.net_whole_shares} shares`
 );

 if (!revocationResult.success) {
 return { success: false, error: `Failed to revoke old certificates: ${revocationResult.error}` };
 }

 // Phase 4b: the PERSONAL/base-entity set now covers ONLY the personal remainder
 // (net_whole_shares - company_total). Company shares are certificated separately
 // per legal entity below. certificatesNeeded is derived inside the helper.
 const generatedCertificates: ConsolidatedCertificate[] = [];

 // Reuse KYC data from gate check above (already verified non-null)
 const kycData = kycGateData;

 // Parse address and name components
 let addressLine1 = '';
 let addressLine2 = '';
 let city = '';
 let country = '';
 let postalCode = '';
 let overrideName = '';

 // For company accounts, use company registered address
 if (isCompanyCertificate && companyData) {
 console.log('Using company address for certificate');
 addressLine1 = companyData.registeredAddress.street;
 addressLine2 = companyData.registeredAddress.province || '';
 city = companyData.registeredAddress.city;
 country = companyData.registeredAddress.countryCode;
 postalCode = companyData.registeredAddress.postalCode;
 } else {
 // Check for user-level address/name overrides first via API
 const overrideResult = await callCertificateAPI('getAddressOverride', { userId });
 const addressOverride = overrideResult.data;

 if (addressOverride) {
 addressLine1 = addressOverride.address_line1 || '';
 addressLine2 = addressOverride.address_line2 || '';
 city = addressOverride.city || '';
 country = addressOverride.country || '';
 postalCode = addressOverride.postal_code || '';
 // Check for name override
 if (addressOverride.full_name) {
 overrideName = addressOverride.full_name;
 } else if (addressOverride.first_name && addressOverride.last_name) {
 overrideName = `${addressOverride.first_name} ${addressOverride.last_name}`;
 }
 } else if (kycData) {
 // Parse from KYC data - using correct field names from kyc_information table
 addressLine1 = kycData.street_address || kycData.address_line1 || '';
 addressLine2 = kycData.province || kycData.suburb || kycData.address_line2 || ''; // Province is state/region
 city = kycData.city || '';
 country = kycData.country_name || kycData.country || ''; // country_name is the correct field
 postalCode = kycData.postal_code || '';
 }
 }

 // Decrypt ID number from KYC data (base64 encoded)
 const decryptIdNumber = (encryptedId: string | null | undefined): string => {
 try {
 if (!encryptedId) return 'Not provided';
 // Use atob for browser-based base64 decoding
 const decoded = atob(encryptedId);
 console.log('Successfully decoded ID:', decoded);
 return decoded;
 } catch (e) {
 console.error('Error decrypting ID number:', e, 'Input:', encryptedId);
 return encryptedId || 'Not provided';
 }
 };

 // For company certificates, use registration number instead of personal ID
 let userIdNumber: string;
 if (isCompanyCertificate && companyData) {
 userIdNumber = companyData.registrationNumber;
 console.log('Using company registration number:', userIdNumber);
 } else {
 userIdNumber = decryptIdNumber(kycData?.id_number_encrypted);
 console.log('Final userIdNumber:', userIdNumber, 'from encrypted:', kycData?.id_number_encrypted);
 }

 // Get certificate recipient name
 // For company accounts: use company legal name
 // For individuals: priority is override > KYC > user profile
 let userName: string;
 if (isCompanyCertificate && companyData) {
 userName = companyData.legalName;
 console.log('Using company legal name:', userName);
 } else {
 userName = overrideName ||
 kycData?.full_legal_name ||
 (kycData?.first_name && kycData?.last_name
 ? `${kycData.first_name} ${kycData.last_name}`
 : null) ||
 user.full_name;
 }

 // (1) PERSONAL / base-entity certificate set — covers ONLY the personal remainder.
 // For an account_type==='company' director the base entity is still the primary-
 // director company (unchanged behaviour); otherwise it is the individual. Skipped
 // entirely when the director has NO personal shares (a pure-company holder).
 if (personalWholeShares >= 1) {
 const baseSet = await this.generateEntityCertificateSet({
 userIdForPng: user.id,
 wholeShares: personalWholeShares,
 userName,
 userIdNumber,
 addressLine1,
 addressLine2,
 city,
 country,
 postalCode,
 address,
 isCompanyCertificate,
 companyId: isCompanyCertificate && companyData ? companyData.id : null,
 companyName: isCompanyCertificate && companyData ? companyData.legalName : null,
 companyRegistrationNumber: isCompanyCertificate && companyData ? companyData.registrationNumber : null,
 companyRegisteredAddress: isCompanyCertificate && companyData ? companyData.registeredAddress : null,
 shareBreakdown
 });
 if (!baseSet.success) {
 return { success: false, error: baseSet.error, share_breakdown: shareBreakdown };
 }
 generatedCertificates.push(...(baseSet.certificates || []));
 }

 // (2) One SEPARATE certificate set per COMPANY legal entity (per-purchase COMPANY
 // attribution). Each uses the company's legal name / registration number / address
 // and sets is_company_certificate + company_id/name/reg so the shares are recorded
 // against the COMPANY, never the director personally.
 for (const bucket of companyBuckets) {
 if (bucket.company_shares < 1) continue;
 const companySet = await this.generateEntityCertificateSet({
 userIdForPng: user.id,
 wholeShares: bucket.company_shares,
 userName: bucket.legal_name || 'Company',
 userIdNumber: bucket.registration_number || 'N/A',
 addressLine1: bucket.address?.street || '',
 addressLine2: bucket.address?.province || '',
 city: bucket.address?.city || '',
 country: bucket.address?.countryCode || '',
 postalCode: bucket.address?.postalCode || '',
 address: bucket.address?.street || address,
 isCompanyCertificate: true,
 companyId: bucket.company_id,
 companyName: bucket.legal_name,
 companyRegistrationNumber: bucket.registration_number,
 companyRegisteredAddress: bucket.address,
 shareBreakdown
 });
 if (!companySet.success) {
 return { success: false, error: companySet.error, share_breakdown: shareBreakdown };
 }
 generatedCertificates.push(...(companySet.certificates || []));
 }

 // Send notification to user
 await this.notifyUserCertificatesReady(userId, generatedCertificates, shareBreakdown);

 return {
 success: true,
 certificates: generatedCertificates,
 revoked_certificates: revocationResult.revoked_count > 0 ? [`${revocationResult.revoked_count} certificates revoked`] : [],
 share_breakdown: shareBreakdown
 };

 } catch (error) {
 console.error('Error generating consolidated certificates:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error occurred'
 };
 }
 }

 /**
 * Phase 4b (Design A): generate a consolidated certificate SET for ONE legal entity
 * (either the director's personal/base entity or a single company). Contains the
 * cert-number + PNG + insert loop that is identical for every entity; the entity's
 * name / id-or-registration / address and the company attribution fields are passed
 * in so the SAME logic issues both personal and per-company certificates without
 * duplication. Splits wholeShares across MAX_SHARES_PER_CERTIFICATE-sized certs.
 */
 private async generateEntityCertificateSet(params: {
 userIdForPng: number;
 wholeShares: number;
 userName: string;
 userIdNumber: string;
 addressLine1: string;
 addressLine2: string;
 city: string;
 country: string;
 postalCode: string;
 address: string;
 isCompanyCertificate: boolean;
 companyId: string | null;
 companyName: string | null;
 companyRegistrationNumber: string | null;
 companyRegisteredAddress: any | null;
 shareBreakdown: ShareCalculationBreakdown;
 }): Promise<{ success: boolean; certificates?: ConsolidatedCertificate[]; error?: string }> {
 const certificates: ConsolidatedCertificate[] = [];
 const certificatesNeeded = Math.ceil(params.wholeShares / this.MAX_SHARES_PER_CERTIFICATE);
 let remainingShares = params.wholeShares;

 for (let i = 0; i < certificatesNeeded; i++) {
 const sharesForThisCert = Math.min(remainingShares, this.MAX_SHARES_PER_CERTIFICATE);

 // Get next sequential certificate number using API
 const certNumResult = await callCertificateAPI('getNextCertNumber', { sharesCount: sharesForThisCert });

 if (certNumResult.error || !certNumResult.data || certNumResult.data.length === 0) {
 console.error('Error generating certificate number:', certNumResult.error);
 return { success: false, error: 'Failed to generate certificate number' };
 }

 const certNo = certNumResult.data[0].cert_no; // e.g., "0-000-001"
 const refNo = certNumResult.data[0].ref_no; // e.g., "1-4000"

 const issueDate = new Date().toLocaleDateString('en-GB', {
 day: '2-digit',
 month: '2-digit',
 year: 'numeric'
 });

 // Generate certificate PNG using SVG generator
 const generationResult = await svgCertificateGenerator.generateConsolidatedCertificatePNG({
 userId: params.userIdForPng,
 certificateNumber: certNo,
 sharesCount: sharesForThisCert,
 userName: params.userName,
 userIdNumber: params.userIdNumber,
 addressLine1: params.addressLine1,
 addressLine2: params.addressLine2,
 city: params.city,
 country: params.country,
 postalCode: params.postalCode,
 issueDate
 });

 if (!generationResult.pngBlob || !generationResult.pngUrl) {
 return {
 success: false,
 error: `Certificate PNG generation failed for certificate ${i + 1}`
 };
 }

 // Save to database with proper cert_no and ref_no fields via API
 // Include company fields if this is a company certificate
 const insertResult = await callCertificateAPI('insertCertificate', {
 certificateData: {
 user_id: params.userIdForPng,
 purchase_id: null,
 certificate_number: certNo,
 cert_no: certNo,
 ref_no: refNo,
 shares_count: sharesForThisCert,
 shares_at_issuance: sharesForThisCert,
 cumulative_shares_start: parseInt(refNo.split('-')[0]) || 0,
 cumulative_shares_end: parseInt(refNo.split('-')[1]) || 0,
 status: 'active',
 approval_status: 'pending_approval', // Requires admin approval before user can see it
 physical_address: params.address,
 certificate_type: 'consolidated',
 is_legacy: false,
 // Company certificate fields
 is_company_certificate: params.isCompanyCertificate,
 company_id: params.isCompanyCertificate ? params.companyId : null,
 company_name: params.isCompanyCertificate ? params.companyName : null,
 company_registration_number: params.isCompanyCertificate ? params.companyRegistrationNumber : null,
 certificate_data: {
 file_url: generationResult.pngUrl,
 file_format: 'PNG',
 generated_at: new Date().toISOString(),
 share_breakdown: params.shareBreakdown,
 is_company_certificate: params.isCompanyCertificate,
 company_details: params.isCompanyCertificate ? {
 company_id: params.companyId,
 legal_name: params.companyName,
 registration_number: params.companyRegistrationNumber,
 registered_address: params.companyRegisteredAddress
 } : null
 }
 }
 });

 if (insertResult.error || !insertResult.data) {
 console.error('Error saving certificate:', insertResult.error);
 return { success: false, error: 'Failed to save certificate to database' };
 }

 certificates.push(insertResult.data as ConsolidatedCertificate);
 remainingShares -= sharesForThisCert;
 }

 return { success: true, certificates };
 }

 /**
 * Generate verification URL for certificate
 */
 private __generateVerificationUrl(certificateNumber: string, userId: number): string {
 const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'https://aureus.africa';
 return `${baseUrl}/verify-certificate/${certificateNumber}?user=${userId}`;
 }

 /**
 * Upload certificate file to storage
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 private async __uploadCertificateFile(
 certificateNumber: string,
 userId: number,
 buffer: Buffer,
 format: string
 ): Promise<string | null> {
 try {
 const fileName = `certificates/consolidated_${certificateNumber}_${userId}.${format}`;
 const contentType = format === 'pdf' ? 'application/pdf' : 'image/png';

 // Convert buffer to base64 for API transmission
 const fileContent = buffer.toString('base64');

 const uploadResult = await callCertificateAPI('uploadCertificateFile', {
 fileName,
 fileContent,
 contentType
 });

 if (!uploadResult.success) {
 console.error('Storage upload error:', uploadResult.error);
 return null;
 }

 // Get public URL via API
 const urlResult = await callCertificateAPI('getCertificatePublicUrl', { fileName });
 return urlResult.publicUrl || null;

 } catch (error) {
 console.error('Error uploading certificate file:', error);
 return null;
 }
 }

 /**
 * Send notification to user when certificates are ready
 */
 private async notifyUserCertificatesReady(
 userId: number,
 certificates: ConsolidatedCertificate[],
 shareBreakdown: ShareCalculationBreakdown
 ): Promise<void> {
 try {
 const certCount = certificates.length;
 const totalShares = shareBreakdown.net_whole_shares;

 await notificationService.createNotification({
 user_id: userId,
 type: 'share_purchase',
 title: ' Your Consolidated Share Certificates are Ready!',
 message: `Your ${certCount} consolidated certificate${certCount > 1 ? 's' : ''} representing ${totalShares} shares ${certCount > 1 ? 'have' : 'has'} been generated and ${certCount > 1 ? 'are' : 'is'} now available for download. ${certCount > 1 ? 'These certificates represent' : 'This certificate represents'} your total share ownership including all purchases and transfers.`,
 metadata: {
 certificate_count: certCount,
 total_shares: totalShares,
 certificate_numbers: certificates.map(c => c.certificate_number),
 certificate_type: 'consolidated',
 share_breakdown: shareBreakdown
 }
 });
 } catch (error) {
 console.error('Error sending certificate notification:', error);
 }
 }

 /**
 * Get active certificates for a user
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 async getUserActiveCertificates(userId: number): Promise<ConsolidatedCertificate[]> {
 try {
 const result = await callCertificateAPI('getUserActiveCertificates', { userId });

 if (result.error) {
 console.error('Error fetching active certificates:', result.error);
 return [];
 }

 return result.data || [];

 } catch (error) {
 console.error('Error getting user active certificates:', error);
 return [];
 }
 }

 /**
 * Get revoked certificates for a user (certificate history)
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 async getUserRevokedCertificates(userId: number): Promise<ConsolidatedCertificate[]> {
 try {
 const result = await callCertificateAPI('getUserRevokedCertificates', { userId });

 if (result.error) {
 console.error('Error fetching revoked certificates:', result.error);
 return [];
 }

 return result.data || [];

 } catch (error) {
 console.error('Error getting user revoked certificates:', error);
 return [];
 }
 }

 /**
 * Batch generate consolidated certificates for all users with shares
 */
 async batchGenerateAllUserCertificates(): Promise<{
 success: boolean;
 total_users: number;
 successful: number;
 failed: number;
 errors: string[];
 }> {
 try {
 const { users: userBalances, incomplete, error } = await this.getAllUserShareBalances();
 if (incomplete) {
 return {
 success: false,
 total_users: userBalances.length,
 successful: 0,
 failed: 0,
 errors: [`Certificate book load is incomplete. Do not batch-generate.${error ? ' ' + error : ''}`]
 };
 }
 const result = {
 success: true,
 total_users: userBalances.length,
 successful: 0,
 failed: 0,
 errors: [] as string[]
 };

 for (const userBalance of userBalances) {
 try {
 const genResult = await this.generateConsolidatedCertificates(
 userBalance.user_id,
 userBalance.physical_address || undefined
 );

 if (genResult.success) {
 result.successful++;
 } else {
 result.failed++;
 result.errors.push(`User ${userBalance.username} (ID: ${userBalance.user_id}): ${genResult.error}`);
 }
 } catch (error) {
 result.failed++;
 result.errors.push(`User ${userBalance.username} (ID: ${userBalance.user_id}): ${error instanceof Error ? error.message : 'Unknown error'}`);
 }
 }

 return result;

 } catch (error) {
 console.error('Error in batch certificate generation:', error);
 return {
 success: false,
 total_users: 0,
 successful: 0,
 failed: 0,
 errors: [error instanceof Error ? error.message : 'Unknown error']
 };
 }
 }

 /**
 * Approve a certificate and send email notification to user
 * Uses API endpoints - NO getServiceRoleClient() in browser
 */
 async approveCertificate(certificateId: string): Promise<{
 success: boolean;
 error?: string;
 certificate?: ConsolidatedCertificate;
 }> {
 try {
 // First get the certificate details via API
 const certResult = await callCertificateAPI('getCertificateById', {
 certificateId,
 includeUser: true
 });

 if (certResult.error || !certResult.data) {
 console.error('Error fetching certificate:', certResult.error);
 return { success: false, error: 'Certificate not found' };
 }

 const cert = certResult.data;

 // Update approval status with timestamp via API
 const updateResult = await callCertificateAPI('updateCertificate', {
 certificateId,
 updates: {
 approval_status: 'approved',
 approved_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 }
 });

 if (updateResult.error) {
 console.error('Error updating certificate approval:', updateResult.error);
 return { success: false, error: 'Failed to update approval status' };
 }

 // Send approval email to user
 const userEmail = cert.users?.email;
 const userName = cert.users?.full_name || cert.users?.username || 'Shareholder';

 if (userEmail) {
 try {
 console.log('Sending certificate approval email to:', userEmail);

 const emailResponse = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 emailType: 'certificate_approval',
 data: {
 email: userEmail,
 fullName: userName,
 certificateNumber: cert.certificate_number || cert.cert_no,
 sharesCount: cert.shares_count || cert.shares_at_issuance,
 dashboardUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://aureus.africa'}/dashboard`
 }
 })
 });

 const emailResult = await emailResponse.json();
 if (!emailResult.success) {
 console.warn('Email sending failed:', emailResult.error);
 } else {
 console.log('Certificate approval email sent successfully');
 }
 } catch (emailError) {
 console.error('Error sending approval email:', emailError);
 // Don't fail the approval if email fails
 }
 }

 // Also send a notification
 try {
 await notificationService.createNotification({
 user_id: cert.user_id,
 type: 'share_purchase',
 title: ' Certificate Approved!',
 message: `Your share certificate ${cert.certificate_number || cert.cert_no} has been approved and is now available in your Portfolio under the Certificates tab.`,
 priority: 'high',
 read: false,
 metadata: {
 certificate_id: certificateId,
 certificate_number: cert.certificate_number || cert.cert_no,
 shares_count: cert.shares_count
 }
 } as any);
 } catch (notifError) {
 console.error('Error creating notification:', notifError);
 }

 return { success: true, certificate: updateResult.data };

 } catch (error) {
 console.error('Error approving certificate:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }
}

// Export singleton instance
export const consolidatedCertificateService = new ConsolidatedCertificateService();
export default consolidatedCertificateService;
